import { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Snackbar, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { messagesApi } from "../../api/resources";
import { useLanguage } from "../../i18n/LanguageContext";
import { COLORS } from "../../theme";

const courierLabel = (p) => (p.courier === "Otro" && p.courierNote ? `Otro (${p.courierNote})` : p.courier);
const trackingUrl = (pkg) => `${window.location.origin}/rastreo/${pkg.trackingToken}`;

export default function ConversationDialog({ pkg, onClose, onSent }) {
  const { t, lang } = useLanguage();
  const [messages, setMessages] = useState(pkg?.messages || []);
  const [status, setStatus] = useState(pkg?.status);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    setMessages(pkg?.messages || []);
    setStatus(pkg?.status);
    setText("");
    setError("");
  }, [pkg?._id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  if (!pkg) return null;

  const closed = status === "entregado";

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      const { package: updated } = await messagesApi.send(pkg._id, text.trim());
      setMessages(updated.messages);
      setText("");
      onSent?.(updated);
    } catch (err) {
      setError(err.response?.data?.error || t("conversation.errGeneric"));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(trackingUrl(pkg));
    setCopied(true);
  };

  return (
    <Dialog open={!!pkg} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {t("conversation.title")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {pkg.unitId} · {pkg.occupantId?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {courierLabel(pkg)} — {pkg.desc}
            </Typography>
          </Box>
          <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyLink}>
            {t("conversation.copyLink")}
          </Button>
        </Stack>

        {closed && <Alert severity="info" sx={{ mb: 2 }}>{t("conversation.closedNotice")}</Alert>}

        <Stack spacing={1.5} sx={{ maxHeight: 320, overflowY: "auto", mb: 2, pr: 0.5 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
              {t("conversation.noMessages")}
            </Typography>
          )}
          {messages.map((m) => (
            <Box
              key={m._id}
              sx={{
                alignSelf: m.senderRole === "staff" ? "flex-end" : "flex-start",
                maxWidth: "80%",
                bgcolor: m.senderRole === "staff" ? COLORS.brassSoft : "#F4F5EF",
                borderRadius: 2,
                px: 1.5, py: 1,
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" fontWeight={600}>{m.senderName}</Typography>
                {m.senderRole === "staff" && <Chip label={t("conversation.staffBadge")} size="small" sx={{ height: 14, fontSize: 9 }} />}
              </Stack>
              <Typography variant="body2">{m.content}</Typography>
              <Typography variant="caption" color="text.secondary">{fmtDateTime(m.sentAt)}</Typography>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Stack>

        {!closed && (
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("conversation.replyPlaceholder")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              multiline
              maxRows={4}
            />
            <Button variant="contained" onClick={handleSend} disabled={sending || !text.trim()} sx={{ minWidth: 44, px: 1.5 }}>
              <SendIcon fontSize="small" />
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>

      <Snackbar open={copied} autoHideDuration={2500} onClose={() => setCopied(false)}>
        <Alert severity="success" onClose={() => setCopied(false)}>{t("conversation.linkCopied")}</Alert>
      </Snackbar>
    </Dialog>
  );
}
